/**
 * activityLabel — 활동 로그의 API 경로를 사람이 읽는 행동명으로 바꾼다.
 *
 * 사장 피드백(2026-07-26): "활동로그가 있는 건 좋은데 알아보고 파악하기 힘들다".
 * `PUT /api/estimate/quick-v2/6e1a0e8f-…` 같은 원문은 읽는 사람에게 정보가 아니다.
 *
 * 규칙 출발점 = 실기록 전수 조회(209건·9패턴). 그 위에 주요 쓰기 엔드포인트를 덮었고,
 * 매칭 실패해도 경로 첫 조각 + 동사로 최소한의 뜻은 남긴다(모르는 건 숨기지 않는다).
 * 원문 경로는 화면에서 title(툴팁)로 유지 — 추적이 필요할 때 원본을 잃지 않게.
 */

/** [메서드(null=전부), 경로 정규식, 행동명] — 위에서부터 먼저 맞는 것. 구체적인 걸 위로. */
const RULES = [
  // 위성 앱 접속(보안 웨이브 — 발급·핑이 접속 기록을 겸한다, 2026-07-28)
  ["POST", /^\/api\/staff\/ping#proposal\b/, "제안서 접속"],
  ["POST", /^\/api\/staff\/firestore-token#schedule\b/, "공정표·계약 데이터 접속"],
  ["POST", /^\/api\/staff\/firestore-token#sites\b/, "현장관리 데이터 접속"],
  ["POST", /^\/api\/staff\/firestore-token#survey\b/, "실측보고서 데이터 접속"],
  ["POST", /^\/api\/staff\/ping#/, "위성 앱 접속"],
  ["POST", /^\/api\/staff\/firestore-token\b/, "위성 앱 데이터 접속"],
  // 견적
  ["POST", /^\/api\/qcc\/calc\b/, "견적 체크 실행"],
  ["POST", /^\/api\/estimate\/[^/]+\/[^/]+\/copy$/, "견적 복사"],
  // 정산은 견적 하위 경로다(/api/estimate/{id}/settlement…) — 아래 견적 규칙보다 위여야
  // '견적 생성'으로 잘못 접히지 않는다(2026-07-28 대조: /api/settlement 는 없는 경로였다).
  [null, /^\/api\/estimate\/[^/]+\/settlement\b/, "정산 처리"],
  ["POST", /^\/api\/estimate\b/, "견적 생성"],
  ["PUT", /^\/api\/estimate\b/, "견적 저장"],
  ["DELETE", /^\/api\/estimate\b/, "견적 삭제"],
  [null, /^\/api\/franchise\b/, "프랜차이즈 견적 저장"],
  // 계약·전자계약
  ["POST", /^\/api\/contract\/sign-request\/[^/]+\/revoke$/, "전자계약 요청 회수"],
  ["POST", /^\/api\/contract\/[^/]+\/sign-request$/, "전자계약 서명 요청 발송"],
  [null, /^\/api\/contract\/render-pdf/, "계약서 PDF 생성"],
  [null, /^\/api\/contract\b/, "계약서 저장"],
  ["DELETE", /^\/api\/sign-requests?\b/, "전자계약 요청 삭제"],
  [null, /^\/api\/sign-requests?\b/, "전자계약 처리"],
  [null, /^\/api\/balance-sheets?\b/, "잔금내역서 저장"],
  // 고객 포털·AS
  [null, /^\/api\/portal-admin\/issue$/, "고객 포털 링크 발급"],
  ["PATCH", /^\/api\/as\/admin\/requests\b/, "AS 접수 처리"],
  [null, /^\/api\/as\b/, "AS 접수"],
  // 영업·정산·머니
  [null, /^\/api\/pipeline\b/, "영업 단계 변경"],
  [null, /^\/api\/money\/bank\/upload$/, "SMBM 은행내역 업로드"],
  [null, /^\/api\/money\/tax\/upload$/, "SMBM 계산서 업로드"],
  [null, /^\/api\/money\b/, "SMBM 처리"],
  // 설정·마스터
  ["PUT", /^\/api\/suppliers\b/, "사업자 정보 수정"],
  [null, /^\/api\/suppliers\b/, "사업자 정보 변경"],
  [null, /^\/api\/recipe-db\b/, "단가 DB 수정"],
  [null, /^\/api\/brands\b/, "브랜드 수정"],
  [null, /^\/api\/(work)?docs?\b/, "기타문서 저장"],
  [null, /^\/api\/share-links?\b/, "공유 링크 처리"],
  [null, /^\/api\/trash\b/, "휴지통 처리"],
  [null, /^\/api\/staff\/invite\b/, "직원 초대 발급"],
  // 실경로는 이메일이 중간에 낀다(/api/staff/{email}/approve) — 2026-07-28 대조 수정
  [null, /^\/api\/staff\/[^/]+\/approve$/, "직원 승인"],
  [null, /^\/api\/staff\/[^/]+\/block$/, "직원 정지"],
  [null, /^\/api\/staff\b/, "직원 관리"],
  [null, /^\/api\/settings\b/, "설정 변경"],
];

const VERB = { POST: "등록", PUT: "수정", PATCH: "변경", DELETE: "삭제" };

/** 메서드+경로 → 한글 행동명. 모르는 경로도 빈 문자열을 내지 않는다. */
export function describeActivity(method, path) {
  const m = String(method || "").toUpperCase();
  const p = String(path || "").split("?")[0];
  for (const [wantMethod, re, label] of RULES) {
    if ((!wantMethod || wantMethod === m) && re.test(p)) return label;
  }
  const seg = p.replace(/^\/api\//, "").split("/")[0];
  return seg ? `${seg} ${VERB[m] || m}`.trim() : (VERB[m] || m || "활동");
}

/**
 * 연속된 같은 사람·같은 행동을 한 줄로 접는다 — 견적 자동저장이 실기록의 67%(140/209)라
 * 접지 않으면 목록이 그 도배로 덮여 나머지가 안 보인다.
 * 입력은 최신순 정렬 가정(백엔드 규약) → 뒤로 갈수록 과거이므로 since 를 갱신한다.
 */
export function groupActivities(items) {
  const out = [];
  for (const a of items || []) {
    const action = describeActivity(a.method, a.path);
    const n = Math.max(1, Number(a.count) || 1);   // 서버가 이미 병합한 횟수
    const target = String(a.target || "");
    const last = out[out.length - 1];
    if (last && last.email === a.email && last.action === action && last.target === target) {
      last.count += n;
      last.since = a.at;
      continue;
    }
    out.push({ email: a.email || "", action, target, count: n,
               at: a.at || "", since: a.at || "",
               method: a.method || "", path: a.path || "" });
  }
  return out;
}
